import { FormControl, Grid, IconButton, MenuItem, Select } from '@material-ui/core';
import Collapse from '@material-ui/core/Collapse';
import AddIcon from '@material-ui/icons/Add';
import ExpandLessRoundedIcon from '@material-ui/icons/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@material-ui/icons/ExpandMoreRounded';
import cx from 'classnames';
import Color from 'color';
import TrashIcon from 'components/base/icons/TrashIcon';
import { icon_reset } from 'components/base/images';
import CircularLoading from 'components/base/loading/CircularLoading';
import MuiButton from 'components/common/button/MuiButton';
import MuiWrapper from 'components/common/input/MuiWrapper';
import MultidiePlate from 'components/common/multidie/MultidiePlate';
import { StyledPlainButtonOuter } from 'components/common/styled/Button';
import TeethSvgV2 from 'components/common/teeth/TeethSvgV2';
import { T } from 'components/common/text';
import CustomSpan from 'components/common/text/CustomSpan';
import CustomText from 'components/common/text/CustomText';
import SelectedTeethList from 'components/project/SelectedTeethList';
import useCheckSetInput from 'lib/hooks/useCheckSetInput';
import useInput from 'lib/hooks/useInput';
import { addCommas } from 'lib/library';
import { userCustomOption } from 'lib/mapper';
import {
  ceramicMetalIconList,
  occlusalSurfaceIconList,
  restorationPonticIconList,
} from 'lib/teeth/teethDesignMapper';
import { IPartnerInfo } from 'lib/types/invoiceTypes';
import { IProjectInfo, IToothShade, IndicationSeq, Tooth } from 'lib/types/projectTypes';
import { useDidUpdateEffect } from 'lib/utils';
import moment from 'moment';
import React, { ChangeEvent, Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InvoiceActions, ProjectActions, UtilActions } from 'store/actionCreators';
import { useShallowAppSelector } from 'store/hooks';
import styled, { createGlobalStyle } from 'styled-components';
import { color } from 'styles/utils';

interface UnitPrice {
  id: number;
  unitPrice: number;
  content: string;
  quantity: number;
  isModify: boolean;
  isRemove?: boolean;
  isCustom: number;
  toothNumberGroup: string;
  unitKey: string;
}

interface CompleteProjectModalContainerProps {
  isModifyFlag?: boolean;
  projectCode: string;
  onCancel: () => void;
}

interface Unit {
  content: string;
  quantity: number;
  unitPrice: number;
}

interface ProjectInvoice {
  projectIdx: number;
  projectId: string;
  unitList: Unit[];
}

interface IndicationInfoToothShade {
  id: string;
  list: {
    seq: number;
    name: string;
  }[];
}

// resolve
function CompleteProjectModalContainer({
  isModifyFlag,
  projectCode,
  onCancel,
}: CompleteProjectModalContainerProps) {
  const {
    language,
    user,
    fetchCurrencyList: { currencyList },
    indicationFormat,
    indicationFormatSuccess,
    indicationInfo,
    indicationInfoSuccess,
    invoiceInitData,
    invoiceInitDataSuccess,
    invoiceInitDataPending,
    invoiceSummary,
    invoiceSummarySuccess,
    invoiceSummaryPending,
    createInvoiceSuccess,
    createSummarySuccess,
    editSummarySuccess,
    projectData,
    projectDataSuccess,
    projectDataPending,
    indicationOptionOffline,
    indicationOptionOfflineSuccess,
  } = useShallowAppSelector(state => ({
    language: state.base.language,
    user: state.user.user,
    fetchCurrencyList: state.util.currencyList.data || {},
    indicationFormat: state.util.indicationFormat.data?.indicationFormat,
    indicationFormatSuccess: state.util.indicationFormat.success,
    indicationInfo: state.util.indicationInfo.data?.indicationInfo,
    indicationInfoSuccess: state.util.indicationInfo.success,
    invoiceInitData: state.invoice.invoiceSummaryInit.data || {},
    invoiceInitDataSuccess: state.invoice.invoiceSummaryInit.success,
    invoiceInitDataPending: state.invoice.invoiceSummaryInit.pending,
    invoiceSummary: state.invoice.invoiceSummary.data || {},
    invoiceSummarySuccess: state.invoice.invoiceSummary.success,
    invoiceSummaryPending: state.invoice.invoiceSummary.pending,
    createInvoiceSuccess: state.invoice.createInvoice.success,
    createSummarySuccess: state.invoice.createSummary.success,
    editSummarySuccess: state.invoice.editSummary.success,
    projectData: state.project.project.data,
    projectDataSuccess: state.project.project.success,
    projectDataPending: state.project.project.pending,
    indicationOptionOffline: state.util.indicationOptionOffline.data,
    indicationOptionOfflineSuccess: state.util.indicationOptionOffline.success,
  }));

  const { t } = useTranslation();
  const inFormatIndication: IndicationSeq[] = indicationFormat?.indication;
  const currency = useInput('');
  const invoiceDataCt = useInput();
  const partnerGroupIdx = useInput();
  const partnerInfo: IPartnerInfo = invoiceDataCt.value?.partnershipInfo || {};
  const { partnerName, currencyCode } = partnerInfo;
  const projectListItem = Array.isArray(invoiceDataCt.value?.projectList)
    ? invoiceDataCt.value?.projectList[0]
    : {};
  const projectInfo: IProjectInfo = projectData?.projectInfo;
  const [isProjectCreator, setIsProjectCreator] = useState<boolean>(false);

  const multiDieTeeth = useInput([]);
  // init request api
  const projectId: string = projectData?.projectInfo.projectId;
  const projectName: string = projectData?.projectInfo.projectName;
  const dueDate: any = projectData?.projectInfo.dueDate;
  const enrollDate: any = projectData?.projectInfo.enrollDate;
  const clientName: string = projectData?.projectInfo.clientName;
  const manager: string = projectData?.projectInfo.manager;
  const isMultidie: number = projectData?.projectInfo.isMultidie;

  const teethValue = projectData?.projectInfo?.indication?.teeth;
  const bridge = projectData?.projectInfo.indication.bridgeList;
  const numbering = projectData?.projectInfo.indication.notation;
  const restorationPonticDesign = projectData?.projectInfo.indication.restorationPonticDesign;
  const occlusalSurfaceDesign = projectData?.projectInfo.indication.occlusalSurfaceDesign;
  const ceramicMetalDesign = projectData?.projectInfo.indication.ceramicMetalDesign;
  const toothShade = projectData?.projectInfo.indication.shade.code;
  const teeth = useInput(!!teethValue ? [...teethValue] : []);

  const invoiceStatusList = {
    published: 'published',
    exist: 'exist',
    new: 'new',
  };

  // const {
  //   groupList,
  //   partList,
  //   materialList,
  //   implantList,
  //   implantAbutmentMaterialList,
  //   implantScanbodyScanList,
  // } = indicationInfo;

  useEffect(() => {
    multiDieTeeth.setValue(teeth.value);
  }, [teeth.value]);

  // state
  const tooth = useInput({
    number: null,
  });
  const checkTooth = useCheckSetInput(new Set([]));
  const teethListTableRef = useRef<HTMLDivElement | null>(null);
  const selectedRestorationPontic = restorationPonticIconList.find(
    item => item.id === restorationPonticDesign,
  );
  const selectedceramicMatel = ceramicMetalIconList.find(item => item.id === ceramicMetalDesign);
  const selectedOcclusalSurface = occlusalSurfaceIconList.find(
    item => item.id === occlusalSurfaceDesign,
  );

  const [invoiceStatus, setInvoiceStatus] = useState('');
  const [checkedSummaryExpand, setCheckedSummaryExpand] = useState(false);
  const [checkedInvoiceExpand, setCheckedInvoiceExpand] = useState(true);
  // invoice state
  const unitListBodyRef = useRef<HTMLDivElement | any>(null);
  const [unitPriceList, setUnitPriceList] = useState<UnitPrice[] | any>([]);
  const [prevUnitPriceList, setPrevUnitPriceList] = useState<UnitPrice[] | any>([]);
  const [newUnitIndex, setNewUnitIndex] = useState<number>(0);

  const [isModify, setIsModify] = useState(false);

  const [newUnitList, setNewUnitList] = useState<UnitPrice[] | any>([]);
  const [isValidSave, setIsValidSave] = useState<boolean>(false);

  const totalPrice = useMemo(() => {
    let newUnitPriceTotal = 0;
    let unitPriceTotal = 0;
    if (!!newUnitList && newUnitList.length > 0) {
      newUnitPriceTotal =
        newUnitList?.reduce((acc: number, curr: any) => {
          return (acc = acc + curr.unitPrice * curr.quantity);
        }, 0) || 0;
    }

    if (!!unitPriceList && unitPriceList.length > 0) {
      unitPriceTotal =
        unitPriceList?.reduce((acc: number, curr: UnitPrice) => {
          return (acc = acc + curr.unitPrice * curr.quantity);
        }, 0) || 0;
    }
    return unitPriceTotal + newUnitPriceTotal;
  }, [unitPriceList, newUnitList]);

  const teethIsExpanded = useMemo(() => {
    return teeth.value?.map((item: Tooth) => {
      return { number: item?.number, isExpanded: false };
    });
  }, [teeth]);
  const selectedTeethIsExpanded = useInput(teethIsExpanded);
  const [allIndicationOption, setAllIndicationOption] = useState(null);

  const materialShadeList = useMemo(() => {
    if (!indicationInfo) return;

    return [...indicationInfo?.materialShadeList].reduce(
      (acc: IToothShade[], curr: IndicationInfoToothShade) => {
        let currArr = [{ group: curr.id }, ...curr.list];
        return acc.concat(currArr);
      },
      [],
    );
  }, [indicationInfo?.materialShadeList]);

  // SECTION: function
  const handleChangeUnitContent = ({ id, content }: { id: number; content: string }) => {
    setUnitPriceList((draft: any[]) => {
      return draft.reduce((acc, curr) => {
        return acc.concat(curr.id === id ? { ...curr, content } : curr);
      }, [] as object[]);
    });
  };

  const handleChangeNewUnitContent = ({ id, content }: { id: number; content: string }) => {
    setNewUnitList((draft: any[]) => {
      return draft.reduce((acc, curr) => {
        return acc.concat(curr.id === id ? { ...curr, content } : curr);
      }, [] as object[]);
    });
  };

  const handleChangeUnitQuantity = ({
    id,
    quantity,
  }: {
    id: number;
    quantity: number | string;
  }) => {
    setUnitPriceList((draft: any[]) => {
      return draft.reduce((acc, curr) => {
        return acc.concat(curr.id === id ? { ...curr, quantity } : curr);
      }, [] as object[]);
    });
  };

  const handleChangeNewUnitQuantity = ({
    id,
    quantity,
  }: {
    id: number;
    quantity: number | string;
  }) => {
    setNewUnitList((draft: any[]) => {
      return draft.reduce((acc, curr) => {
        return acc.concat(curr.id === id ? { ...curr, quantity } : curr);
      }, [] as object[]);
    });
  };

  const handleChangeUnitPrice = ({ id, unitPrice }: { id: number; unitPrice: number | string }) => {
    if (isNaN(Number(unitPrice))) return;
    setUnitPriceList((draft: any[]) => {
      return draft.reduce((acc, curr) => {
        return acc.concat(curr.id === id ? { ...curr, unitPrice } : curr);
      }, [] as object[]);
    });
  };

  const handleChangeNewUnitPrice = ({
    id,
    unitPrice,
  }: {
    id: number;
    unitPrice: number | string;
  }) => {
    if (isNaN(Number(unitPrice))) return;
    setNewUnitList((draft: any[]) => {
      return draft.reduce((acc, curr) => {
        return acc.concat(curr.id === id ? { ...curr, unitPrice } : curr);
      }, [] as object[]);
    });
  };

  const handleRemoveUnit = (id: number) => {
    setUnitPriceList((draft: any[]) => {
      return draft.filter(item => {
        return item.id !== id;
      }, [] as object[]);
    });
  };
  const handleRemoveNewUnit = (id: number) => {
    setNewUnitList((draft: any[]) => {
      return draft.filter(item => {
        return item.id !== id;
      }, [] as object[]);
    });
  };

  const handleToggleSummaryExpand = () => {
    setCheckedSummaryExpand(!checkedSummaryExpand);
    setCheckedInvoiceExpand(false);
  };

  const handleToggleInvoiceExpand = () => {
    setCheckedInvoiceExpand(!checkedInvoiceExpand);
    setCheckedSummaryExpand(false);
  };

  const handleModifyInvoice = () => {
    setIsModify(true);
    setPrevUnitPriceList(unitPriceList);
  };

  const handleChangeCurrency = (value: string | any) => {
    currency.setValue(value);

    const submitData = {
      projectCode,
      partnerGroupIdx: partnerGroupIdx.value,
      currency,
    };

    if (projectInfo?.summaryInfo.isExist) {
      InvoiceActions.fetch_invoice_summary_request(submitData);
    } else {
      InvoiceActions.fetch_invoice_summary_init_request(submitData);
    }
  };

  const handleEditSummary = () => {
    // if (!unitPriceList.length) return;

    let unitList = [];
    if (!newUnitList?.length) {
      unitList = [...unitPriceList];
    } else {
      unitList = [...unitPriceList, ...newUnitList];
    }

    setPrevUnitPriceList(unitList);
    const submitData = {
      projectCode,
      data: {
        currency: currency.value,
        unitList,
      },
    };
    InvoiceActions.edit_project_summary_request(submitData);
  };

  const handleSummarySave = () => {
    // if (!unitPriceList?.length) return;
    // let unitList = [];
    // if (!newUnitList?.length) {
    //   unitList = [...unitPriceList];
    // } else {
    //   unitList = [...unitPriceList, ...newUnitList];
    // }
    const submitData = {
      projectCode,
      data: {
        currency: currency.value,
        unitList: [...unitPriceList, ...newUnitList],
      },
    };
    InvoiceActions.create_project_summary_request(submitData);
  };

  const handleSummaryCancel = () => {
    if (!!isModifyFlag) {
      onCancel();
    }
    setIsModify(false);
    setUnitPriceList(prevUnitPriceList);
    setNewUnitList((draft: any[]) => {
      return !draft;
    });
  };

  const handleAddContentNewUnit = (index: number) => {
    const { projectIdx } = projectListItem;
    const unitDefaultForm = {
      id: unitPriceList?.length ? unitPriceList.length + index + 1 : index + 1,
      content: '',
      quantity: 0,
      unitPrice: 0,
      isModify: true,
      currency: currencyCode,
      projectIdx: projectIdx,
      isRemove: false,
      isNew: true,
      isCustom: 1,
    };
    setNewUnitIndex(unitPriceList?.length + index + 1);
    if (!newUnitList?.length) {
      setNewUnitList([unitDefaultForm]);
    } else {
      setNewUnitList((draft: any) => [...draft, unitDefaultForm]);
    }
  };

  const handleInvoiceReset = () => {
    let submitPartnerGroupIdx = null;
    let submitCurrency = '';

    if (user?.groupCode === projectInfo?.ownerGroupCode) {
      setIsProjectCreator(true);
      partnerGroupIdx.setValue(projectInfo.clientIdx);
      submitPartnerGroupIdx = projectInfo.clientIdx;
      submitCurrency = projectInfo.clientCurrencyCode;
    } else {
      setIsProjectCreator(false);
      partnerGroupIdx.setValue(projectInfo.ownerIdx);
      submitPartnerGroupIdx = projectInfo.ownerIdx;
      submitCurrency = projectInfo.ownerCurrencyCode;
    }

    const submitData = {
      projectCode,
      partnerGroupIdx: submitPartnerGroupIdx,
      currency: submitCurrency,
    };
    InvoiceActions.fetch_invoice_summary_init_request(submitData);
  };

  const handleSelectedIndicationChange =
    (number: number) => (event: ChangeEvent<{}>, expanded: boolean) => {
      if (!selectedTeethIsExpanded) return;

      handleSetTeethExpanded(number, false);
    };

  const handleSetTeethExpanded = (number: number, isOnly?: boolean) => {
    selectedTeethIsExpanded.setValue(
      selectedTeethIsExpanded.value.map((item: any) => {
        if (item.number === number) {
          return { number: number, isExpanded: !item.isExpanded };
        } else {
          return !isOnly ? item : { number: item.number, isExpanded: false };
        }
      }),
    );
  };

  useEffect(() => {
    UtilActions.fetch_teeth_indication_option_offline_request();
  }, []);

  useEffect(() => {
    selectedTeethIsExpanded.setValue(teethIsExpanded);
  }, [teethIsExpanded]);

  useDidUpdateEffect(() => {
    handleSetTeethExpanded(tooth.value.number, true);
  }, [tooth.value]);

  useEffect(() => {
    if (!projectCode) return;
    ProjectActions.fetch_project_request(projectCode);
  }, [projectCode]);

  useDidUpdateEffect(() => {
    if (projectDataSuccess) {
      // 초기 진입 시, projectData get : project complete, invoice수정 시 사용해야함으로, 프로젝트 정보 refetch
      UtilActions.fetch_teeth_indication_info_request({
        groupCode: !!projectInfo.ownerGroupCode ? projectInfo.ownerGroupCode : '',
      });
      UtilActions.fetch_teeth_indication_format_request({
        groupCode: !!projectInfo.ownerGroupCode ? projectInfo.ownerGroupCode : '',
      });
      if (projectInfo?.invoiceInfo.isExist && projectInfo?.summaryInfo.isExist) {
        setInvoiceStatus(invoiceStatusList.published);
      } else if (!projectInfo?.invoiceInfo.isExist && projectInfo?.summaryInfo.isExist) {
        setInvoiceStatus(invoiceStatusList.exist);
        if (!!isModifyFlag) {
          setIsModify(true);
        }
      } else {
        setIsModify(true);
        setInvoiceStatus(invoiceStatusList.new);
      }
      let submitPartnerGroupIdx = null;
      let submitCurrency = '';

      if (user?.groupCode === projectInfo?.ownerGroupCode) {
        setIsProjectCreator(true);
        partnerGroupIdx.setValue(projectInfo.clientIdx);
        submitPartnerGroupIdx = projectInfo.clientIdx;
        submitCurrency = projectInfo.clientCurrencyCode;
      } else {
        setIsProjectCreator(false);
        partnerGroupIdx.setValue(projectInfo.ownerIdx);
        submitPartnerGroupIdx = projectInfo.ownerIdx;
        submitCurrency = projectInfo.ownerCurrencyCode;
      }

      if (projectInfo?.summaryInfo.isExist) {
        const submitData = {
          projectCode,
          partnerGroupIdx: submitPartnerGroupIdx,
          currency: projectInfo?.summaryInfo.currency,
        };
        InvoiceActions.fetch_invoice_summary_request(submitData);
      } else {
        const submitData = {
          projectCode,
          partnerGroupIdx: submitPartnerGroupIdx,
          currency: submitCurrency,
        };
        InvoiceActions.fetch_invoice_summary_init_request(submitData);
      }
    }
  }, [projectDataSuccess, isModifyFlag]);

  useDidUpdateEffect(() => {
    if (!!invoiceInitDataSuccess) {
      // invoice Init 시에만, content 번역(저장 시점의 language로 저장)하고 이미 작성된 content의 경우에는 번역x
      const unitContentTranslate = invoiceInitData.projectList[0]?.unitList?.map(
        (item: UnitPrice) => {
          const contentDashParse = item.content?.split('-');
          const contentCommaParse = contentDashParse[0]?.split(',');
          const contentIndication = contentCommaParse[0];
          const contentMaterial = contentCommaParse[1];

          const indicationAlias = item.unitKey?.split('-')[0];
          const materialAlias = item.unitKey?.split('-')[1];
          const implantAlias = item.unitKey?.split('-')[2];

          const indication =
            indicationAlias === userCustomOption.unknown || !indicationAlias
              ? t('GLOBAL_UNKNOWN')
              : indicationAlias.split('_')[0] === userCustomOption.indication
              ? contentIndication
              : t(`INDICATION_${indicationAlias}`);

          const material =
            materialAlias === userCustomOption.unknown || !materialAlias
              ? t('GLOBAL_UNKNOWN')
              : materialAlias.split('_')[0] === userCustomOption.material
              ? contentMaterial
              : t(`MATERIAL_${materialAlias}`);

          const implant = t(`ABUTMENT_IMPLANT_${implantAlias}`);

          const content = `${indication},${material},${implant}-${contentDashParse[1]}`;
          return { ...item, content };
        },
      );

      const newInvoiceInitData = {
        partnershipInfo: invoiceInitData.partnershipInfo,
        projectList: [{ ...invoiceInitData.projectList[0], unitList: unitContentTranslate }],
      };
      // invoiceDataCt.setValue(invoiceInitData);
      invoiceDataCt.setValue(newInvoiceInitData);

      setNewUnitList([]);
      if (!currency.value) currency.setValue(invoiceInitData.partnershipInfo.currencyCode);
    }
  }, [invoiceInitDataSuccess]);

  useDidUpdateEffect(() => {
    if (!!invoiceSummarySuccess) {
      invoiceDataCt.setValue(invoiceSummary);
      if (!currency.value) currency.setValue(invoiceSummary.projectList[0].currency);
    }
  }, [invoiceSummarySuccess]);

  useDidUpdateEffect(() => {
    const unitPriceListValue = projectListItem?.unitList?.map(
      (item: ProjectInvoice, index: number) => ({
        ...item,
        id: index,
        isModify: false,
        isRemove: false,
        isNew: false,
      }),
    );
    setUnitPriceList(unitPriceListValue);
  }, [projectListItem]);

  useDidUpdateEffect(() => {
    // unitPriceList 추가 시, scroll 이동
    unitListBodyRef.current.scrollTop = unitListBodyRef.current.scrollHeight;
    if (!!unitPriceList?.length) {
      const inputRowElementList = unitListBodyRef.current.querySelectorAll(`[data-id]`);
      const lastElement = inputRowElementList[inputRowElementList.length - 1];
      lastElement.querySelector(`.completeProjectModal__invoice_input.content`).focus();
    }
  }, [newUnitIndex]);

  // reclick setup
  useDidUpdateEffect(() => {
    const isRe = tooth.value.number === undefined;
    checkTooth.onChange({ value: isRe ? tooth.value.reNumber : tooth.value.number });
  }, [tooth.value]);

  // teeth click에 따른 teeth list table scroll 높이 조절
  // useDidUpdateEffect(() => {
  //   if (
  //     teethListTableRef.current &&
  //     !!teeth?.value?.find((item: Tooth) => item.number === tooth.value?.number)
  //   ) {
  //     const rowGutter = 43; // height + marginTop
  //     const currentTeethRow = teethListTableRef.current.querySelector(
  //       `div[data-tooth="${tooth.value.number}"]`,
  //     );
  //     const currentTeethRowPositionTop = (currentTeethRow as HTMLDivElement).offsetTop;
  //     setTimeout(() => {
  //       if (teethListTableRef.current)
  //         teethListTableRef.current.scrollTop = currentTeethRowPositionTop - rowGutter;
  //     }, 250);
  //   }
  // }, [tooth.value, teeth, teethListTableRef.current]);

  useEffect(() => {
    if (
      (!newUnitList || newUnitList.length === 0) &&
      (!unitPriceList || unitPriceList.length === 0)
    ) {
      setIsValidSave(false);
    } else {
      setIsValidSave(true);
    }
  }, [newUnitList, unitPriceList]);
  // close modal when create_invoice status success
  useDidUpdateEffect(() => {
    if (createInvoiceSuccess) {
      onCancel();
    }
  }, [!!createInvoiceSuccess]);

  useDidUpdateEffect(() => {
    if (createSummarySuccess) {
      setInvoiceStatus(invoiceStatusList.exist);
      setIsModify(false);
      onCancel();
    }
  }, [!!createSummarySuccess]);

  useDidUpdateEffect(() => {
    if (editSummarySuccess) {
      if (!!isModifyFlag) {
        onCancel();
      }
      setIsModify(false);
      setUnitPriceList(prevUnitPriceList);
      setNewUnitList((draft: any[]) => {
        return !draft;
      });
    }
  }, [!!editSummarySuccess]);

  useEffect(() => {
    // DidUnMount
    // invoiceInit clear
    return () => {
      InvoiceActions.fetch_invoice_summary_clear();
      InvoiceActions.fetch_invoice_summary_init_clear();
    };
  }, []);

  useEffect(() => {
    if (indicationOptionOfflineSuccess) {
      setAllIndicationOption(indicationOptionOffline);
    }
  }, [indicationOptionOfflineSuccess]);

  return (
    <StyledCompleteProjectModalContainer data-component-name="CompleteProjectModalContainer">
      <div
        className={cx('completeProjectModal__expand_box summary', {
          'border-none': checkedSummaryExpand,
        })}
      >
        <button
          className="completeProjectModal__expand_btn btn-reset"
          onClick={handleToggleSummaryExpand}
        >
          <span className="completeProjectModal__expand_text">
            <T>GLOBAL_SUMMARY</T>
          </span>
          <span className="completeProjectModal__expand_icon_box flex-center">
            {checkedSummaryExpand ? (
              <ExpandLessRoundedIcon htmlColor="inherit" fontSize="inherit" />
            ) : (
              <ExpandMoreRoundedIcon htmlColor="inherit" fontSize="inherit" />
            )}
          </span>
        </button>
      </div>
      <Collapse in={checkedSummaryExpand}>
        <div className="completeProjectModal__summary_container">
          <div className="completeProjectModal__summary_info_box">
            <CustomText
              className="completeProjectModal__summary_info_projectId"
              fontSize={14}
              fontWeight={500}
              color={color.navy_blue}
            >
              {projectName}
            </CustomText>
            <ul>
              <li>
                <p className="completeProjectModal__summary_info_label">
                  <T>PROJECT_ID</T>
                </p>
                <p className="completeProjectModal__summary_info_text">{projectId}</p>
              </li>
              <li>
                <p className="completeProjectModal__summary_info_label">
                  <T>GLOBAL_CLIENT</T>
                </p>
                <p className="completeProjectModal__summary_info_text">
                  {partnerName || clientName}
                </p>
              </li>
              <li>
                <p className="completeProjectModal__summary_info_label">
                  <T>GLOBAL_PROJECT_DATE</T>
                </p>
                <p className="completeProjectModal__summary_info_text">
                  {moment(enrollDate).isValid()
                    ? moment
                        .unix(enrollDate)
                        .locale(language)
                        .format(language === 'en' ? 'MMM DD, YYYY' : 'YYYY. MM. DD ')
                    : '-'}
                </p>
              </li>
              <li>
                <p className="completeProjectModal__summary_info_label">
                  <T>GLOBAL_PROJECT_DUEDATE</T>
                </p>
                <p className="completeProjectModal__summary_info_text">
                  {moment(dueDate).isValid()
                    ? moment
                        .unix(dueDate)
                        .locale(language)
                        .format(language === 'en' ? 'MMM DD, YYYY' : 'YYYY. MM. DD ')
                    : '-'}
                </p>
              </li>
              <li>
                <p className="completeProjectModal__summary_info_label">
                  <T>PROJECT_MANAGER</T>
                </p>
                <p className="completeProjectModal__summary_info_text">{manager}</p>
              </li>
            </ul>
          </div>

          <Grid container className="completeProjectModal__summary_teeth_grid_container">
            <Grid item xs={5}>
              <div
                className={cx('completeProjectModal__teeth_box', {
                  isMultidie: isMultidie,
                })}
              >
                {!!isMultidie ? (
                  <MultidiePlate isEdit={false} tooth={tooth} teeth={multiDieTeeth} isComplete />
                ) : (
                  <TeethSvgV2
                    isEdit={false}
                    tooth={tooth}
                    numbering={{ value: numbering }}
                    teeth={{ value: teeth.value }}
                    bridge={{ value: bridge }}
                  />
                )}
              </div>
            </Grid>
            <Grid item xs={7}>
              <div className="completeProjectModal__tooth_shade_box">
                <Grid container className="completeProjectModal__tooth_shade_item">
                  <Grid item className="completeProjectModal__tooth_shade_label">
                    <T>PROJECT_TOOTH_SHADE</T>
                  </Grid>
                  <Grid item className="completeProjectModal__tooth_shade_contents">
                    {toothShade}
                  </Grid>
                </Grid>
              </div>
              <div className="completeProjectModal__tooth_list" ref={teethListTableRef}>
                {!!indicationInfo && (
                  <SelectedTeethList
                    isViewer={true}
                    isMultidie={isMultidie}
                    numbering={numbering}
                    teeth={teeth}
                    bridge={bridge}
                    selectedTeethIsExpanded={selectedTeethIsExpanded}
                    indicationInfo={indicationInfo}
                    allIndicationOption={allIndicationOption}
                    materialShadeList={materialShadeList}
                    // onDeleteTooth={onDeleteTooth}
                    onSelectedIndicationChange={handleSelectedIndicationChange}
                  />
                )}
              </div>
              <ul className="completeProjectModal__tooth_design_list">
                {selectedRestorationPontic && (
                  <li className="completeProjectModal__tooth_design_item">
                    <figure className="pontic">
                      <img src={selectedRestorationPontic?.src} alt="tooth design" />
                    </figure>
                    <div className="completeProjectModal__tooth_design_name">
                      <T>SUMMARY_PONTIC_DESIGN</T>
                    </div>
                  </li>
                )}
                {selectedceramicMatel && (
                  <li className="completeProjectModal__tooth_design_item">
                    <figure className="ceramic">
                      <img src={selectedceramicMatel?.src} alt="tooth design" />
                    </figure>
                    <div className="completeProjectModal__tooth_design_name">
                      <T>SUMMARY_MATEL_DESIGN</T>
                    </div>
                  </li>
                )}
                {selectedOcclusalSurface && (
                  <li className="completeProjectModal__tooth_design_item">
                    <figure className="occlusal">
                      <img src={selectedOcclusalSurface?.src} alt="tooth design" />
                    </figure>
                    <div className="completeProjectModal__tooth_design_name">
                      <T>SUMMARY_SURFACE_DESIGN</T>
                    </div>
                  </li>
                )}
              </ul>
            </Grid>
          </Grid>
        </div>
      </Collapse>

      <div
        className={cx('completeProjectModal__expand_box invoice', {
          'border-none': checkedInvoiceExpand,
        })}
      >
        <button
          className="completeProjectModal__expand_btn btn-reset"
          onClick={handleToggleInvoiceExpand}
        >
          <span className="completeProjectModal__expand_text">
            <T>GLOBAL_INVOICE</T>
          </span>
          <span className="completeProjectModal__expand_icon_box flex-center">
            {checkedInvoiceExpand ? (
              <ExpandLessRoundedIcon htmlColor="inherit" fontSize="inherit" />
            ) : (
              <ExpandMoreRoundedIcon htmlColor="inherit" fontSize="inherit" />
            )}
          </span>
        </button>
      </div>
      <Collapse in={checkedInvoiceExpand}>
        <div className="completeProjectModal__invoice_container">
          <div className="completeProjectModal__invoice_button_box">
            {isModify && currencyList?.length > 0 && (
              <>
                <MuiButton
                  config={{
                    width: '100px',
                    color: '#b5b7c1',
                    fontColor: `${color.white}`,
                  }}
                  disabled={false}
                  disableElevation
                  color="primary"
                  variant="contained"
                  disablebackground={color.navy_blue}
                  disablefontcolor="#ffffff"
                  className="completeProjectModal__invoice_reset_btn"
                  onClick={handleInvoiceReset}
                >
                  <img src={icon_reset} />
                  <span>
                    <T>GLOBAL_RESET</T>
                  </span>
                </MuiButton>
                <MuiWrapper className="completeProjectModal__invoice_currency_wrapper">
                  <FormControl fullWidth variant="outlined">
                    <StyledGlobalCurrencySelect />
                    <Select
                      MenuProps={{
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left',
                        },

                        getContentAnchorEl: null,
                      }}
                      name="currency"
                      value={currency.value}
                      onChange={e => {
                        handleChangeCurrency(e.target.value);
                      }}
                    >
                      {currencyList?.map((item: any, index: number) => {
                        return (
                          <MenuItem
                            value={item.code}
                            key={index}
                            className={cx('completeProjectModal__currency_select_item_container', {
                              selected: currency.value === item.code ? true : false,
                            })}
                          >
                            <div
                              className={cx('completeProjectModal__currency_select_item_box', {
                                selected: currency.value === item.code ? true : false,
                              })}
                            >
                              <div>
                                {item.feature} {item.name}
                              </div>
                              {invoiceDataCt.value?.partnershipInfo.currencyCode === item.code && (
                                <div
                                  className={cx('completeProjectModal__currency_select_default', {
                                    selected: currency.value === item.code ? true : false,
                                  })}
                                >
                                  <T>GLOBAL_MAIN</T>
                                </div>
                              )}
                            </div>
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </MuiWrapper>
              </>
            )}
          </div>
          <div className="completeProjectModal__invoice_info_box">
            <div className="completeProjectModal__invoice_info_card">
              <div className="completeProjectModal__invoice_info_card_partner">
                <div className="completeProjectModal__invoice_info_card_partner_to flex-center">
                  <T>GLOBAL_INVOICE_TO</T>
                </div>
                <CustomText
                  className="completeProjectModal__invoice_info_card_partner_name"
                  fontSize={21}
                  fontWeight={700}
                  marginTop={25}
                >
                  {partnerName}
                </CustomText>
              </div>

              <div className="completeProjectModal__invoice_info_card_date">
                <CustomText fontColor={color.gray_week}>
                  <T>GLOBAL_PROJECT_DATE</T>
                </CustomText>
                <CustomText fontSize={19} fontWeight={500} marginTop={10}>
                  {moment(enrollDate).isValid()
                    ? language === 'en'
                      ? moment.unix(enrollDate).locale(language).format('MMM DD, YYYY')
                      : moment.unix(enrollDate).locale(language).format('YYYY. MM. DD ')
                    : '-'}
                </CustomText>
              </div>

              <div className="completeProjectModal__invoice_info_card_date">
                <CustomText fontColor={color.gray_week}>
                  <T>GLOBAL_PROJECT_DUEDATE</T>
                </CustomText>
                <CustomText fontSize={19} fontWeight={500} marginTop={10}>
                  {moment(dueDate).isValid()
                    ? language === 'en'
                      ? moment.unix(dueDate).locale(language).format('MMM DD, YYYY')
                      : moment.unix(dueDate).locale(language).format('YYYY. MM. DD ')
                    : '-'}
                </CustomText>
              </div>
            </div>
          </div>
          <div className="completeProjectModal__invoice_items_box">
            <div className="completeProjectModal__invoice_items_table">
              <div className="completeProjectModal__invoice_items_thead">
                <div className="completeProjectModal__invoice_items_row">
                  <div className="completeProjectModal__invoice_items_th">
                    <T>PROJECT_ID</T>
                  </div>
                  <div className="completeProjectModal__invoice_items_th">
                    <T>GLOBAL_CONTENT</T>
                  </div>
                  <div className="completeProjectModal__invoice_items_th">
                    <T>GLOBAL_QUANTITY</T>
                  </div>
                  <div className="completeProjectModal__invoice_items_th">
                    <T>GLOBAL_UNIT_PRICE</T>
                  </div>
                  <div className="completeProjectModal__invoice_items_th">
                    <T>GLOBAL_PRICE</T>
                  </div>
                  <div className="completeProjectModal__invoice_items_th"></div>
                </div>
              </div>

              <div className="completeProjectModal__invoice_items_tbody" ref={unitListBodyRef}>
                <div className="completeProjectModal__invoice_items_row">
                  <div className="completeProjectModal__invoice_items_td project_id">
                    {projectId}
                  </div>
                </div>

                {!!invoiceInitDataPending || !!invoiceSummaryPending || !!projectDataPending ? (
                  <div className="completeProjectModal__invoice_item_progress">
                    <CircularLoading size={40} />
                  </div>
                ) : (
                  <>
                    {!!unitPriceList?.length &&
                      unitPriceList.map((item: UnitPrice, idx: number) => {
                        return (
                          <Fragment key={idx}>
                            {item.isRemove ? (
                              ''
                            ) : (
                              <div
                                className="completeProjectModal__invoice_items_row"
                                data-id={item.id}
                              >
                                <div className="completeProjectModal__invoice_items_td"></div>
                                <div className="completeProjectModal__invoice_items_td">
                                  {!!isModify && !!item.isCustom ? (
                                    <div className="completeProjectModal__invoice_input_wrapper">
                                      <textarea
                                        className="completeProjectModal__invoice_input content input_box"
                                        placeholder=""
                                        value={item.content || ''}
                                        onKeyDown={e => {
                                          if (e.code === 'Enter') e.preventDefault();
                                        }}
                                        onChange={e => {
                                          if (e.target.value.length > 100) return;
                                          handleChangeUnitContent({
                                            id: item.id,
                                            content: e.target.value,
                                          });
                                        }}
                                      ></textarea>
                                    </div>
                                  ) : (
                                    <div className="completeProjectModal__invoice_input content ">
                                      {item.content || ''}
                                    </div>
                                  )}
                                </div>
                                <div className="completeProjectModal__invoice_items_td">
                                  <input
                                    type="text"
                                    readOnly={!isModify}
                                    placeholder="0"
                                    value={item.quantity ? addCommas(item.quantity) : ''}
                                    onChange={e => {
                                      if (e.target.value.length > 2) return;
                                      handleChangeUnitQuantity({
                                        id: item.id,
                                        quantity: Number(e.target.value.replace(/,/g, '')),
                                      });
                                    }}
                                    className="completeProjectModal__invoice_input price"
                                  />
                                </div>
                                <div className="completeProjectModal__invoice_items_td">
                                  <input
                                    type="text"
                                    readOnly={!isModify}
                                    placeholder="0"
                                    value={item.unitPrice ? addCommas(item.unitPrice) : ''}
                                    onChange={e => {
                                      if (e.target.value.length > 11) return;
                                      handleChangeUnitPrice({
                                        id: item.id,
                                        unitPrice: Number(e.target.value.replace(/,/g, '')),
                                      });
                                    }}
                                    className="completeProjectModal__invoice_input price"
                                  />
                                </div>
                                <div className="completeProjectModal__invoice_items_td">
                                  {
                                    currencyList?.find((item: any) => item.code === currency.value)
                                      ?.feature
                                  }{' '}
                                  {addCommas(item.unitPrice * item.quantity)}
                                </div>
                                <div className="completeProjectModal__invoice_items_td">
                                  {isModify && (
                                    <IconButton
                                      aria-label="cancel modal"
                                      onClick={() => handleRemoveUnit(item.id)}
                                    >
                                      <TrashIcon />
                                    </IconButton>
                                  )}
                                </div>
                              </div>
                            )}
                          </Fragment>
                        );
                      })}
                    {!!newUnitList?.length &&
                      newUnitList.map((item: UnitPrice, idx: number) => {
                        return (
                          <Fragment key={idx}>
                            {item.isRemove ? (
                              ''
                            ) : (
                              <div
                                className="completeProjectModal__invoice_items_row"
                                data-id={item.id}
                              >
                                <div className="completeProjectModal__invoice_items_td"></div>
                                <div className="completeProjectModal__invoice_items_td">
                                  <div className="completeProjectModal__invoice_input_wrapper">
                                    <textarea
                                      className="completeProjectModal__invoice_input content input_box"
                                      placeholder=""
                                      value={item.content || ''}
                                      onKeyDown={e => {
                                        if (e.code === 'Enter') e.preventDefault();
                                      }}
                                      onChange={e => {
                                        if (e.target.value.length > 100) return;
                                        handleChangeNewUnitContent({
                                          id: item.id,
                                          content: e.target.value,
                                        });
                                      }}
                                    ></textarea>
                                  </div>
                                </div>
                                <div className="completeProjectModal__invoice_items_td">
                                  <input
                                    type="text"
                                    readOnly={!isModify}
                                    placeholder="0"
                                    value={item.quantity ? addCommas(item.quantity) : ''}
                                    onChange={e => {
                                      if (e.target.value.length > 2) return;
                                      handleChangeNewUnitQuantity({
                                        id: item.id,
                                        quantity: Number(e.target.value.replace(/,/g, '')),
                                      });
                                    }}
                                    className="completeProjectModal__invoice_input price"
                                  />
                                </div>
                                <div className="completeProjectModal__invoice_items_td">
                                  <input
                                    type="text"
                                    maxLength={10}
                                    readOnly={!isModify}
                                    placeholder="0"
                                    value={item.unitPrice ? addCommas(item.unitPrice) : ''}
                                    onChange={e => {
                                      if (e.target.value.length > 11) return;
                                      handleChangeNewUnitPrice({
                                        id: item.id,
                                        unitPrice: Number(e.target.value.replace(/,/g, '')),
                                      });
                                    }}
                                    className="completeProjectModal__invoice_input price"
                                  />
                                </div>
                                <div className="completeProjectModal__invoice_items_td">
                                  {
                                    currencyList?.find((item: any) => item.code === currency.value)
                                      ?.feature
                                  }{' '}
                                  {addCommas(item.unitPrice * item.quantity)}
                                </div>
                                <div className="completeProjectModal__invoice_items_td">
                                  {isModify && (
                                    <IconButton
                                      aria-label="cancel modal"
                                      onClick={() => handleRemoveNewUnit(item.id)}
                                    >
                                      <TrashIcon />
                                    </IconButton>
                                  )}
                                </div>
                              </div>
                            )}
                          </Fragment>
                        );
                      })}
                    {isModify && (
                      <div className="createInvoiceModal__receipt_items_plus_icon_container">
                        <button
                          className="createInvoiceModal__receipt_items_plus_icon_box btn-reset inline-flex-center"
                          onClick={() => {
                            if (!newUnitList?.length) {
                              handleAddContentNewUnit(0);
                            } else {
                              handleAddContentNewUnit(newUnitList.length);
                            }
                          }}
                        >
                          <AddIcon
                            htmlColor="white"
                            fontSize="inherit"
                            className="createInvoiceModal__receipt_items_plus_icon"
                          />
                        </button>
                        <CustomSpan fontColor="#b5b7c1" marginLeft={10}>
                          <T>ADD_CONTENT</T>
                        </CustomSpan>
                      </div>
                    )}
                  </>
                )}
              </div>

              <Grid
                container
                alignItems="center"
                justifyContent="flex-end"
                className="completeProjectModal__invoice_final_price"
              >
                <CustomSpan fontSize={18} fontWeight={500} marginRight={100}>
                  <T>GLOBAL_TOTAL</T>
                </CustomSpan>
                <CustomSpan fontSize={21} fontWeight={700}>
                  {useMemo(() => {
                    const feature = currencyList?.find(
                      (item: any) => item.code === currency.value,
                    )?.feature;
                    return feature;
                  }, [currency.value])}
                  {addCommas(totalPrice)}
                </CustomSpan>
              </Grid>
            </div>
          </div>
        </div>
      </Collapse>

      <StyledPlainButtonOuter backgroundColor={color.navy_blue} height={76} left={'50%'}>
        <div className="completeProjectModal__publish_btn_box">
          {useMemo(
            () => (
              <>
                {!invoiceStatus && (
                  // 데이터 로딩중 표시할 버튼
                  <MuiButton
                    config={{
                      width: '340px',
                      color: '#b5b7c1',
                      fontColor: `${color.white}`,
                    }}
                    disabled={true}
                    disableElevation
                    color="primary"
                    variant="contained"
                    disablebackground={color.navy_blue}
                    disablefontcolor="#ffffff"
                    className="xl border-radius-round inset-shadow-default completeProjectModal__published_btn"
                  ></MuiButton>
                )}
                {invoiceStatus === 'published' && (
                  <MuiButton
                    config={{
                      width: '340px',
                      color: '#b5b7c1',
                      fontColor: `${color.white}`,
                    }}
                    disabled={true}
                    disableElevation
                    color="primary"
                    variant="contained"
                    disablebackground={color.navy_blue}
                    disablefontcolor="#ffffff"
                    className="xl border-radius-round inset-shadow-default completeProjectModal__published_btn"
                  >
                    <T>PUBLISHED_INVOICE</T>
                  </MuiButton>
                )}
                {invoiceStatus === 'exist' && !isModify && (
                  <MuiButton
                    config={{
                      width: '340px',
                    }}
                    disableElevation
                    color="primary"
                    variant="contained"
                    disablebackground={color.navy_blue}
                    disablefontcolor="#ffffff"
                    className="xl border-radius-round inset-shadow-default completeProjectModal__modify_btn"
                    onClick={handleModifyInvoice}
                  >
                    <T>GLOBAL_MODIFY</T>
                  </MuiButton>
                )}
                {invoiceStatus === 'exist' && isModify && (
                  <>
                    <MuiButton
                      config={{
                        width: '94px',
                        color: '#b5b7c1',
                        fontColor: `${color.white}`,
                      }}
                      disableElevation
                      variant="contained"
                      className="xl border-radius-round inset-shadow-default completeProjectModal__reject_btn"
                      onClick={handleSummaryCancel}
                    >
                      <T>GLOBAL_CANCEL</T>
                    </MuiButton>
                    <MuiButton
                      config={{
                        width: '255px',
                      }}
                      disableElevation
                      variant="contained"
                      className="xl border-radius-round inset-shadow-default completeProjectModal__accept_btn"
                      disabled={!isValidSave}
                      onClick={handleEditSummary}
                    >
                      <span className="btn-shadow-inset"></span>
                      <T>GLOBAL_SAVE</T>
                    </MuiButton>
                  </>
                )}
                {invoiceStatus === 'new' && (
                  <MuiButton
                    config={{
                      width: '340px',
                    }}
                    disableElevation
                    color="primary"
                    variant="contained"
                    disablebackground={color.navy_blue}
                    disablefontcolor="#ffffff"
                    className="xl border-radius-round inset-shadow-default completeProjectModal__new_btn"
                    disabled={!isValidSave}
                    // newUnitList
                    // unitPriceList
                    onClick={() => {
                      handleSummarySave();
                    }}
                  >
                    <T>GLOBAL_SAVE_CLOSE</T>
                  </MuiButton>
                )}
              </>
            ),
            [invoiceStatus, isModify, unitPriceList, newUnitList, isValidSave],
          )}
        </div>
      </StyledPlainButtonOuter>
    </StyledCompleteProjectModalContainer>
  );
}

const StyledGlobalCurrencySelect = createGlobalStyle<{}>`

.completeProjectModal__currency_select_item_container {
  &.selected {
    background-color: #00A6E2 !important;
  }
  .completeProjectModal__currency_select_item_box {
  
    width: 100%;
    padding-right: 20px;
    display: flex;
    justify-content: space-between;
    font-weight: 400;
    color: #303030;
    &.selected {
      color: #ffffff;
    }
    .completeProjectModal__currency_select_default {
      width: 70px;
      height: 25px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 13px;
  
      border: 1px solid #69c9ee;
      border-radius: 13px;
      color: #69c9ee;
      font-weight: 400;
      &.selected {
        color: #ffffff;
        border: 1px solid #ffffff;
      }
  
    }
  }
}
`;

const StyledCompleteProjectModalContainer = styled.div`
  .completeProjectModal__banner_box {
    height: 240px;
    background-color: #fafafd;
    border-radius: 15px;
  }

  .completeProjectModal__expand_box {
    position: relative;
    width: 100%;
    height: 45px;
    border: 1px solid ${color.gray_b5};
    border-radius: 3px;

    &.border-none {
      border-width: 0;
    }
    &.invoice {
      margin-top: 65px;
    }
    .completeProjectModal__expand_btn {
      display: flex;
      align-items: center;
      position: relative;
      left: -1px;
      top: -25px;
      height: 50px;
      padding-right: 30px;
      background-color: white;
      text-align: left;

      img {
        margin-right: 10px;
      }
      .completeProjectModal__expand_text {
        display: inline-block;
        width: 110px;
        font-size: 20px;
        font-weight: 700;
        color: ${color.navy_blue};
      }
      .completeProjectModal__expand_icon_box {
        width: 34px;
        height: 34px;
        background-color: ${color.navy_blue};
        border-radius: 50%;
        color: white;
        font-size: 34px;
        line-height: 0;
      }
    }
  }

  .completeProjectModal__summary_container {
    display: flex;
    border: 1px solid #f4f5fa;
    border-radius: 5px;

    .completeProjectModal__summary_info_box {
      width: 240px;
      padding: 30px 20px;
      text-overflow: ellipsis;
      word-break: break-all;
      .completeProjectModal__summary_info_projectId {
        text-overflow: ellipsis;
        word-break: break-all;
      }
      > ul {
        margin-top: 30px;
        border-top: 1px solid #dcdde2;
        li {
          margin-top: 20px;
          list-style-type: none;
          p {
            margin: 2px 0;
          }
          .completeProjectModal__summary_info_label {
            font-size: 14px;
            font-weight: 500;
            color: #303030;
          }
          .completeProjectModal__summary_info_text {
            font-size: 12px;
          }
        }
      }
    }
    .completeProjectModal__summary_teeth_grid_container {
      width: 960px;
      padding: 20px 0;
      border-radius: 5px;
      box-shadow: 0 0 3px rgba(0, 0, 0, 0.16);

      .completeProjectModal__teeth_box {
        width: 254.5px;
        margin: 0 auto;
        &.isMultidie {
          width: 330px;
        }
      }
      .completeProjectModal__tooth_shade_box {
        margin-bottom: 20px;
        .completeProjectModal__tooth_shade_item {
          border: solid 1px #e1e1e1;
          border-radius: 5px;
          column-gap: 20px;

          .completeProjectModal__tooth_shade_label {
            width: 140px;
            display: flex;
            align-items: center;
            justify-content: center;
            height: 34px;
            font-size: 13px;
            font-weight: 500;
            background-color: #f4f4f4;
          }
          .completeProjectModal__tooth_shade_contents {
            font-size: 13px;
            height: 34px;
            display: flex;
            align-items: center;
          }
        }
      }
      .completeProjectModal__tooth_list {
        position: relative;
        height: 384px;
        overflow-y: overlay;
        overflow-x: hidden;
        .completeProjectModal__tooth_item {
          & + .completeProjectModal__tooth_item {
            margin-top: 15px;
          }
          .completeProjectModal__tooth_number {
            display: flex;
            align-items: center;
            font-size: 16px;
            cursor: pointer;
            .completeProjectModal__fold_icon {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              width: 20px;
              height: 20px;
              margin-right: 15px;
              border-radius: 3px;
              border: 1px solid ${color.blue};
              background-color: ${color.blue};
              color: #fff;
              &.unfold {
                background-color: #fff;
                color: ${color.blue};
              }
            }
            .completeProjectModal__fold_color {
              margin-left: 15px;
              width: 16px;
              height: 16px;
            }
          }
          .completeProjectModal__tooth_detail {
            padding-left: 45px;
            height: 0;
            transition: all 0.25s;
            overflow: hidden;
            font-size: 12px;
            &.on {
              height: 100%;
              min-height: 256px;
              max-height: 336px;
              padding-bottom: 5px;
            }
            .completeProjectModal__tooth_detail_grid_container {
              margin-top: 0px;
            }
            .completeProjectModal__tooth_detail_label {
              letter-spacing: -0.3px;
              font-weight: 300;
            }
            .completeProjectModal__tooth_detail_text {
              color: ${color.gray_font};
              overflow: hidden;
              white-space: nowrap;
              text-overflow: ellipsis;
            }
          }
        }
      }
      .completeProjectModal__tooth_design_list {
        display: flex;
        align-items: center;
        margin-top: 5px;
        padding-top: 30px;
        border-top: 1px dashed ${color.gray_da};
        .completeProjectModal__tooth_design_item {
          display: flex;
          align-items: flex-end;
          width: 33.33%;
          figure {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            position: relative;
            width: 40px;
            height: 40px;
            box-shadow: 0 0 3px rgba(0, 0, 0, 0.16);
            border-radius: 5px;
            &.pontic {
              align-items: flex-end;
              img {
                height: 82%;
              }
            }
            &.ceramic img {
              height: 65%;
            }
            &.occlusal img {
              width: 75%;
            }
          }
          .completeProjectModal__tooth_design_name {
            margin-left: 10px;
            width: calc(100% - 50px);
            font-size: 11px;
            line-height: 1.2;
          }
        }
      }
    }
  }

  .completeProjectModal__invoice_container {
    display: flex;
    column-gap: 30px;
    position: relative;
    .completeProjectModal__invoice_button_box {
      position: absolute;
      right: 0;
      top: -45px;
      .completeProjectModal__invoice_reset_btn {
        margin-right: 10px;
        border-radius: 5px;
        height: 35px;
        span {
          margin-left: 2px;
          font-size: 12px;
        }
      }
      .completeProjectModal__invoice_reset_wrapper {
      }
      .completeProjectModal__invoice_currency_wrapper {
        width: 200px;
        height: 35px;
        .completeProjectModal__currency_select_item_box {
          width: 100%;
          padding-right: 4px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-weight: 400;
          &.selected {
            color: #303030;
          }
          .completeProjectModal__currency_select_default {
            font-weight: 400;
            width: 70px;
            height: 25px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 13px;
            border-radius: 13px;
            color: #69c9ee;
            &.selected {
              border: 1px solid #69c9ee;
            }
          }
        }
      }
    }

    .completeProjectModal__invoice_info_box {
      width: 240px;
      .completeProjectModal__invoice_info_card {
        height: 470px;
        padding: 0 30px;
        background-color: ${color.navy_blue};
        border-radius: 10px;
        color: white;
        .completeProjectModal__invoice_info_card_partner {
          padding: 30px 0;
          border-bottom: 3px solid white;
          min-height: 126px;
          .completeProjectModal__invoice_info_card_partner_to {
            width: 120px;
            height: 30px;
            border-radius: 15px;
            border: 1px solid white;
            font-size: 13px;
          }
          .completeProjectModal__invoice_info_card_partner_name {
            text-overflow: ellipsis;
            word-break: break-all;
          }
        }
        .completeProjectModal__invoice_info_card_order_code {
        }
        .completeProjectModal__invoice_info_card_date {
          padding: 25px 0;
        }
      }
    }
    .completeProjectModal__invoice_items_box {
      width: 930px;
      .completeProjectModal__invoice_items_table {
        .completeProjectModal__invoice_item_progress {
          width: 100%;
          height: 350px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .completeProjectModal__invoice_items_th,
        .completeProjectModal__invoice_items_td {
          position: relative;
          min-height: 50px;
          white-space: nowrap;
          line-height: 1;

          &:nth-child(1) {
            width: 260px;
            justify-content: flex-start;
            &.project_id {
              display: flex;
              align-items: center;
              width: 100%;
            }
          }
          &:nth-child(2) {
            width: 280px;
            justify-content: flex-start;
          }
          &:nth-child(3) {
            width: 90px;
            justify-content: flex-start;
          }
          &:nth-child(4) {
            width: 120px;
            justify-content: flex-start;
          }
          &:nth-child(5) {
            width: 120px;
            justify-content: flex-end;
          }
          &:nth-child(6) {
            width: 60px;
            justify-content: flex-start;
          }
        }
      }
      .completeProjectModal__invoice_items_row {
        display: flex;
        align-items: center;
        min-height: 50px;
        .completeProjectModal__invoice_items_td {
          display: flex;
          align-items: center;
        }
      }

      .createInvoiceModal__receipt_items_plus_icon_container {
        height: 50px;
        display: flex;
        align-items: center;
        .createInvoiceModal__receipt_items_plus_icon_box {
          margin-left: 260px;
          margin-right: 5px;
          width: 20px;
          height: 20px;
          background-color: ${color.blue};
          border-radius: 50%;
          font-size: 18px;
        }
      }

      .completeProjectModal__invoice_items_thead {
        border-top: 3px solid #edf4fb;
        border-bottom: 3px solid #edf4fb;
        .completeProjectModal__invoice_items_row {
          display: flex;
          height: 50px;
          div {
            align-items: center;
            display: flex;
          }
        }
      }
      .completeProjectModal__invoice_items_tbody {
        .completeProjectModal__invoice_items_row {
          border-bottom: 1px solid #edf4fb;
        }
      }
      .completeProjectModal__invoice_items_tbody {
        overflow-y: overlay;
        height: 410px;
      }

      .completeProjectModal__invoice_input_wrapper {
        min-height: 70px;
        width: 100%;
        display: flex;
        align-items: center;
        textarea {
          resize: none;
          width: 95%;
          height: 60px;
        }
      }
      .completeProjectModal__invoice_input {
        padding: 5px;
        border: 1px solid transparent;

        &:read-only {
        }
        &:not(:read-only) {
          border-color: ${color.gray_week};
          border-radius: 5px;
        }
        &.content {
          width: 95%;
          display: flex;
          align-items: center;
          word-break: break-all;
          white-space: pre-line;
        }
        &.price {
          width: 95%;
        }
        &.etc {
          margin-left: 5px;
          width: 150px;
          text-align: right;
        }
      }
      .completeProjectModal__invoice_total_price {
        margin-bottom: 15px;
        min-height: 50px;
        font-size: 19px;
        font-weight: 700;
        padding-right: calc(10% + 5px);
        text-align: right;
      }
      .completeProjectModal__invoice_etc_row {
        .completeProjectModal__invoice_etc_td {
          position: relative;
          width: 386px;
          min-height: 50px;
          border-top: 1px solid #edf4fb;
          padding-left: 15px;
          padding-right: 88px;
        }
      }
      .completeProjectModal__invoice_final_price {
        min-height: 50px;
        padding-right: calc(10% + 5px);
        border-top: 3px solid #edf4fb;
      }
    }
  }

  .completeProjectModal__publish_btn_box {
    display: flex;
    position: relative;
    .completeProjectModal__reject_btn {
      position: relative;
      z-index: 2;
    }
    .completeProjectModal__accept_btn {
      margin-left: -10px;
      background: rgb(0, 166, 226);
      background: linear-gradient(90deg, rgba(0, 166, 226, 1) 0%, rgba(8, 123, 238, 1) 100%);
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
      overflow: hidden;
      &:hover {
        background: ${`linear-gradient(
          90deg,
          ${Color('rgba(0, 166, 226, 1)').darken(0.12)} 0%,
          ${Color('rgba(8, 123, 238, 1)').darken(0.12)} 100%
        )`};
      }
      .btn-shadow-inset {
        position: absolute;
        top: -5px;
        left: -46px;
        width: 66px;
        height: 66px;
        border-radius: 33px;
        background-color: ${color.navy_blue};
        box-shadow: 3px 3px 6px rgba(0, 0, 0, 0.16);
      }
    }
  }

  .completeProjectModal__invoice_items_add {
    .createInvoiceModal__receipt_items_plus_icon_box {
      margin-right: 5px;
      width: 20px;
      height: 20px;
      background-color: ${color.blue};
      border-radius: 50%;
      font-size: 18px;
    }
  }
`;

export default React.memo(CompleteProjectModalContainer);
